import { Group, TStartDate } from 'types/misc';
import { DEFAULT_VERTICAL_EXCLUSIONS, getDefaultEndDate } from 'utils/constants';

/**
 * Project Insights (`/projects`) filter selection persisted to the URL under
 * `?f=<compressed>`. Keys are kept short to keep the compressed blob small.
 *
 * Location (`state_code`/`mercator_metropolitan_ids`) is intentionally NOT
 * included here — it is synced separately via the `region-code`/`state-code`
 * params by `useMapLocationFromUrl`. Map center/zoom live in their own
 * `center`/`zoom` params.
 */
export type ProjectFilterUrlState = {
  cc?: string[]; // county_codes
  ci?: number[]; // cityIds
  tg?: Group['triggers']; // triggers (Latest Project Stage)
  pp?: Group['past_project_stage']; // past_project_stage
  ep?: Group['exclude_past_project_stage']; // exclude_past_project_stage
  cs?: string; // company_stakeholder_types
  csx?: string; // company_stakeholder_types_exclusions
  v?: number[]; // verticals
  vx?: number[]; // verticalsExclusions
  wt?: string[]; // workTypes
  wtx?: string[]; // workTypesExclusions
  co?: { min: number; max: number }; // cost
  cu?: boolean; // project_cost_include_unknown
  pmin?: number; // project_size_min
  pmax?: number; // project_size_max
  pinc?: boolean; // project_size_include_unknown
  lmin?: number; // parcel_size_min
  lmax?: number; // parcel_size_max
  linc?: boolean; // parcel_size_include_unknown
  kw?: string; // keywords
  kx?: string; // exclusions
  ck?: string; // companyKeywords
  ckx?: string; // companyKeywordsExclusions
  di?: number[]; // districts
  zx?: number[]; // zoneExclusions
  sd?: TStartDate; // startDate
  ed?: string; // endDate
  dcs?: TStartDate; // date_created_start
  dce?: string; // date_created_end
};

/**
 * Build the compact URL state from the current `groupAtom` value.
 *
 * A key is included only when it DIFFERS from its `DEFAULT_GROUP` value (not
 * merely "non-empty" — several defaults are non-empty, e.g. `verticalsExclusions`,
 * `project_cost_include_unknown: true`, `parcel_size_include_unknown: true`).
 * This keeps the blob genuinely empty (so `useFilterUrlSync` drops the `f`
 * param) for an untouched group, while still round-tripping a user who clears a
 * non-empty default.
 */
export const buildProjectFilterUrlState = (group: Group): ProjectFilterUrlState => {
  const next: ProjectFilterUrlState = {};

  if (group.county_codes.length > 0) next.cc = group.county_codes;
  if (group.cityIds.length > 0) next.ci = group.cityIds;
  if (group.triggers.length > 0) next.tg = group.triggers;
  if (group.past_project_stage.length > 0) next.pp = group.past_project_stage;
  if (group.exclude_past_project_stage.length > 0)
    next.ep = group.exclude_past_project_stage;
  if (group.company_stakeholder_types !== '') next.cs = group.company_stakeholder_types;
  if (group.company_stakeholder_types_exclusions !== '')
    next.csx = group.company_stakeholder_types_exclusions;
  if (group.verticals.length > 0) next.v = group.verticals;
  if (
    JSON.stringify(group.verticalsExclusions) !==
    JSON.stringify(DEFAULT_VERTICAL_EXCLUSIONS)
  )
    next.vx = group.verticalsExclusions;
  if (group.workTypes.length > 0) next.wt = group.workTypes;
  if (group.workTypesExclusions.length > 0) next.wtx = group.workTypesExclusions;
  if (group.cost.min !== 0 || group.cost.max !== 0) next.co = group.cost;
  if (group.project_cost_include_unknown === false) next.cu = false;
  if (group.project_size_min !== null) next.pmin = group.project_size_min;
  if (group.project_size_max !== null) next.pmax = group.project_size_max;
  if (group.project_size_include_unknown === true) next.pinc = true;
  if (group.parcel_size_min !== null) next.lmin = group.parcel_size_min;
  if (group.parcel_size_max !== null) next.lmax = group.parcel_size_max;
  if (group.parcel_size_include_unknown === false) next.linc = false;
  if (group.keywords !== '') next.kw = group.keywords;
  if (group.exclusions && group.exclusions !== '') next.kx = group.exclusions;
  if (group.companyKeywords !== '') next.ck = group.companyKeywords;
  if (group.companyKeywordsExclusions !== '') next.ckx = group.companyKeywordsExclusions;
  if (group.districts.length > 0) next.di = group.districts;
  if (group.zoneExclusions.length > 0) next.zx = group.zoneExclusions;
  if (group.startDate !== 'anytime') next.sd = group.startDate;
  if (group.endDate !== getDefaultEndDate()) next.ed = group.endDate;
  if (group.date_created_start && group.date_created_start !== 'anytime')
    next.dcs = group.date_created_start;
  if (group.date_created_end && group.date_created_end !== getDefaultEndDate())
    next.dce = group.date_created_end;

  return next;
};

/**
 * Reverse of {@link buildProjectFilterUrlState}: translate a decoded URL state
 * back into a partial `Group` to be merged onto the existing group atom
 * (`setGroup(prev => ({ ...prev, ...partial }))`). Only keys present in the
 * decoded state are written, so omitted filters keep their `DEFAULT_GROUP`
 * values.
 */
export const applyProjectFilterUrlState = (
  state: ProjectFilterUrlState,
): Partial<Group> => {
  const partial: Partial<Group> = {};

  if (state.cc !== undefined) partial.county_codes = state.cc;
  if (state.ci !== undefined) partial.cityIds = state.ci;
  if (state.tg !== undefined) partial.triggers = state.tg;
  if (state.pp !== undefined) partial.past_project_stage = state.pp;
  if (state.ep !== undefined) partial.exclude_past_project_stage = state.ep;
  if (state.cs !== undefined) partial.company_stakeholder_types = state.cs;
  if (state.csx !== undefined) partial.company_stakeholder_types_exclusions = state.csx;
  if (state.v !== undefined) partial.verticals = state.v;
  if (state.vx !== undefined) partial.verticalsExclusions = state.vx;
  if (state.wt !== undefined) partial.workTypes = state.wt;
  if (state.wtx !== undefined) partial.workTypesExclusions = state.wtx;
  if (state.co !== undefined) partial.cost = state.co;
  if (state.cu !== undefined) partial.project_cost_include_unknown = state.cu;
  if (state.pmin !== undefined) partial.project_size_min = state.pmin;
  if (state.pmax !== undefined) partial.project_size_max = state.pmax;
  if (state.pinc !== undefined) partial.project_size_include_unknown = state.pinc;
  if (state.lmin !== undefined) partial.parcel_size_min = state.lmin;
  if (state.lmax !== undefined) partial.parcel_size_max = state.lmax;
  if (state.linc !== undefined) partial.parcel_size_include_unknown = state.linc;
  if (state.kw !== undefined) partial.keywords = state.kw;
  if (state.kx !== undefined) partial.exclusions = state.kx;
  if (state.ck !== undefined) partial.companyKeywords = state.ck;
  if (state.ckx !== undefined) partial.companyKeywordsExclusions = state.ckx;
  if (state.di !== undefined) partial.districts = state.di;
  if (state.zx !== undefined) partial.zoneExclusions = state.zx;
  if (state.sd !== undefined) partial.startDate = state.sd;
  if (state.ed !== undefined) partial.endDate = state.ed;
  if (state.dcs !== undefined) partial.date_created_start = state.dcs;
  if (state.dce !== undefined) partial.date_created_end = state.dce;

  return partial;
};
